import { Component, effect, EventEmitter, Injector, Input, OnInit, Output, runInInjectionContext, signal, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { MatSelectSearchComponent, NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { ReplaySubject, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatTooltip } from '@angular/material/tooltip';
import { MatOption } from '@angular/material/core';
import { AsyncPipe } from '@angular/common';

@Component({
    selector: 'app-filter-field-search-select',
    templateUrl: './filter-field-search-select.component.html',
    styleUrls: ['./filter-field-search-select.component.scss'],
    imports: [MatFormField, MatLabel, MatError, MatSelect, FormsModule, ReactiveFormsModule, MatTooltip, MatSelectTrigger, MatOption, NgxMatSelectSearchModule, AsyncPipe]
})
export class FilterFieldSearchSelectComponent<T> implements OnInit {
  private _hidden = signal(false);
  private _disabled = signal(false);

  @Input() compareWith: (o1: any, o2: any) => boolean = (a, b) => a === b;
  @Input() form: FormGroup | null = null;
  @Input() color = 'primary';
  @Input() appearance = 'outline';
  @Input() floatLabel = 'always';
  @Input() multiple = false;
  @Input() controlName = 'field-search-select';
  @Input() label = '';
  @Input() placeholder = 'Make a Selection';
  @Input() toolTipPosition = 'above';
  @Input() selectAllEnabled = false;
  @Input() required = false;
  @Input() noneEnabled = true;
  @Input() idField = 'ID';
  @Input() labelField = 'Description';
  @Input() hideSubscriptWrapper = false;
  @Input() showLabel = true;
  
  @Input() set hidden(value: boolean) {
    this._hidden.set(value);
  }
  @Input() set disabled(value: boolean) {
    this._disabled.set(value);
  }

  get hidden() {
    return this._hidden();
  }
  get disabled() {
    return this._disabled();
  }

  private _options: T[] = [];

  set options(value: T[]){
    if(this._options == value) {
      return;
    }

    this._options = value;
    this.filteredOptions.next(value);
  }

  get options(): T[] {
    return this._options;
  }

  filteredOptions: ReplaySubject<T[]> = new ReplaySubject<T[]>(1);
  control = new FormControl(null);
  filterControl = new FormControl<string>('');
  noneOption = {};
  allToggled = false;
  indeterminateToggle = false;

  subs: Subscription[] = [];

  protected _onDestroy = new Subject<void>();

  @Output() ready = new EventEmitter();
  @Output() valueChanged = new EventEmitter();

  @ViewChild(MatSelect) select: MatSelect;
  @ViewChild(MatSelectSearchComponent) searchFilter: MatSelectSearchComponent;

  constructor(private injector: Injector) { 
  }

  ngOnInit(): void {

    this.noneOption[this.idField] = -1;

    this.form?.addControl(this.controlName, this.control);
    if(this.required) {
      this.control.setValidators([Validators.required]);
    }

    runInInjectionContext(this.injector, () => {
      effect(() => {
        if(this._disabled()) {
          this.control?.disable({emitEvent: false});
        } else {
          this.control?.enable({emitEvent: false});
        }
      })});

    this.filterControl.valueChanges
    .pipe(takeUntil(this._onDestroy))
    .subscribe(() => {
      this.filterMulti();

      if(this.multiple) {
        this.setAllToggleState();
      }
    });

    if(this.multiple) {
      this.control.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.setAllToggleState();
      });
    }
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  setSelectedValues(values?: T[] | null) {

    if(!values) {
      this.control?.reset();
      this.control?.setValue([]);
      this.searchFilter._reset();
      return;
    }

    if(values.length == 0) {
      this.allOptionClicked(true);
      return;
    }

    values.forEach(o => {
      const found = this.select.options.find((i, index) => {
        if(index == 0) {
          return;
        }

        return i.value[this.idField] == o[this.idField];
      });

      if(found) {
        found.select();
      }
    });
  }

  setSingleValueByID(id: any) {
    const item = this.select.options.find(i => {
      if(i.value) {
        return i.value[this.idField] === id;
      }
    });

    this.control.setValue(item?.value || null);
  }

  setToNone() {
    this.control?.setValue(this.noneOption);
  }

  setFirstOption() {
    if (this._options && this._options.length > 0) {
      this.control?.setValue(this._options[0]);
    } else {
      this.control?.setValue(null);
    }
  }

  getValues() {

    if(this.allToggled && this.multiple) {
      const value = {};
      value[this.idField] = 0;
      return [value];
    }

    const controlValue = this.control?.value;

    //Multi mode null check
    if(this.multiple && !controlValue) {
      return [];
    }

    //Single mode none/null check
    if(!this.multiple && !controlValue) {
      return null;
    }
    
    return this.control?.value.map(i => i);
  }

  allOptionClicked(isChecked: boolean) {

    this.select.options.forEach((o, index) => {
      if(index == 0) {
        return;
      }

      isChecked ? o.select() : o.deselect();
    });

  }

  onSelectionChange() {
    this.valueChanged.emit(this.control?.value);
  }

  getTooltipString() {
    let result = '';
    const options = this.control?.value;

    options.forEach((o, index) => {
      result += o[this.labelField];

      if(index < options.length - 1) {
        result += ', ';
      }
    })
    return result;
  }

  getSelectCount() {
    return (this.control.value?.length <= this.options.length) 
      ? this.control.value?.length 
      : this.control.value?.length - 1;
  }

  filterMulti() {
    if (!this._options) {
      return;
    }

    let search = this.filterControl?.value;

    if (!search) {
      this.filteredOptions.next(this._options.slice());
      return;
    } else {
      search = search.toLowerCase();
    }

    this.filteredOptions.next(
      this._options.filter(o => (o[this.labelField].toLowerCase()).indexOf(search.toLowerCase()) > -1)
    );
  }

  setAllToggleState() {
    let filteredLength = 0;
    if(this.control?.value) {
      this._options.forEach(o => {
        if(this.control?.value.indexOf(o) > -1) {
          filteredLength++;
        }
      });

      this.indeterminateToggle = filteredLength > 0 && filteredLength < this._options.length;
      this.allToggled = filteredLength > 0 && filteredLength === this._options.length;
    }
  }

}
